/**
 * The eight universes offered on the create screen, with the icon that
 * stands for each — plus (below) the four story-collection universes that
 * exist only to label and tint catalogue cards.
 *
 * `UNIVERSES` is shared because the generation screen shows the same icon
 * while the story is being written: until the model has produced a title
 * and a cover there is nothing else to look at, and an anonymous striped
 * square told the parent nothing about what was being written (reported on
 * the iOS PWA, 2026-09-19).
 */
import {
  TreePine, Rocket, Anchor, Castle, GraduationCap, Waves, Bone, Wheat,
  Moon, Globe, Lightbulb, Mountain, type LucideIcon,
} from "lucide-react";
import type { CollectionUniverse, DisplayUniverse, Universe } from "./types";

export const UNIVERSES: { key: Universe; icon: LucideIcon; labelKey: string }[] = [
  { key: "forest", icon: TreePine, labelKey: "create.universeForest" },
  { key: "space", icon: Rocket, labelKey: "create.universeSpace" },
  { key: "pirates", icon: Anchor, labelKey: "create.universePirates" },
  { key: "castle", icon: Castle, labelKey: "create.universeCastle" },
  { key: "school", icon: GraduationCap, labelKey: "create.universeSchool" },
  { key: "ocean", icon: Waves, labelKey: "create.universeOcean" },
  { key: "dinosaurs", icon: Bone, labelKey: "create.universeDinosaurs" },
  { key: "farm", icon: Wheat, labelKey: "create.universeFarm" },
];

/**
 * The four story-collection universes (see `CollectionUniverse` in
 * lib/types.ts) — deliberately kept OUT of `UNIVERSES`: that table drives
 * Create.tsx's grid of choices, and every entry in it is something a
 * parent can tap to write a brand-new custom story. These four can't be —
 * there is no writer for them, they only ever arrive already attached to a
 * collection story from the catalogue — so mixing them in would let
 * Create.tsx offer a choice that fails the moment it's submitted.
 */
export const COLLECTION_UNIVERSES: { key: CollectionUniverse; icon: LucideIcon; labelKey: string }[] = [
  { key: "soucis", icon: Moon, labelKey: "catalog.universeSoucis" },
  { key: "monde", icon: Globe, labelKey: "catalog.universeMonde" },
  { key: "inventions", icon: Lightbulb, labelKey: "catalog.universeInventions" },
  { key: "terre", icon: Mountain, labelKey: "catalog.universeTerre" },
];

const DISPLAY_UNIVERSES: { key: DisplayUniverse; icon: LucideIcon; labelKey: string }[] = [
  ...UNIVERSES,
  ...COLLECTION_UNIVERSES,
];

/** Looks up both tables above — used by the generation screen (creatable
 *  universes only, in practice) and anywhere a story's `universe` field
 *  (creatable OR collection) needs its icon/label. */
export function universeByKey(key: string | null | undefined) {
  return DISPLAY_UNIVERSES.find((u) => u.key === key) ?? null;
}
